import { Box, Text } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { canSearchResourceType } from '@/services/permissions';
import { ORBIT_GRAY } from '@/theme/colors';
import { Section } from '../shared/Section';
import { PanelRow } from '../IntakeBilling/PanelRow';
import { insuranceRows } from './insuranceDetails';
import { InsuranceCardRows } from './InsuranceCardRows';
import { canWriteSubscriber, subscriberFormValues } from './subscriber';
import { SubscriberRow } from './SubscriberRow';
import { InsuranceCard } from './useInsuranceCard';

interface InsuranceSectionProps {
  /** `useInsuranceCard`, read once for the page and shared with the Intake and billing panel. */
  card: InsuranceCard;
}

const SECTION_LABELS = { insurer: 'Insurer', memberId: 'Member ID', group: 'Group' };

/**
 * The patient's insurance card: insurer, member ID, group and subscriber, from the insurance
 * Coverage the `update-insurance-coverage` bot edits (BAR-2931).
 *
 * Renders for any patient with an insurance Coverage, whatever their track: an employer-benefit
 * patient, or one told "not yet", keeps their card. A role whose policy can't search Coverage,
 * or a patient with none, gets no section. Default-open, like Intake and billing above it.
 *
 * Insurer, member ID, group (BAR-2932) and the subscriber (BAR-2933) are edited in place by the roles the bot lets edit
 * (`canEditInsuranceCard`); every other role reads the same rows with no edit affordance.
 */
export function InsuranceSection({ card }: InsuranceSectionProps) {
  const medplum = useMedplum();
  const { enabled, state, editable, saveEdit } = card;

  if (!enabled) return null;
  if (state.status === 'unreadable') {
    return (
      <Section title="Insurance" defaultOpen>
        <Text size="sm" c={ORBIT_GRAY} data-testid="insurance-section-unreadable">
          Couldn&apos;t load the insurance card.
        </Text>
      </Section>
    );
  }
  if (state.status !== 'found') return null;

  const { details } = state;
  const rows = insuranceRows(details);
  // Never edited blind: a policyholder that didn't load would have to be retyped, unseen, over the
  // one on file; and a role whose policy can't read policyholders (Finance) would write one it
  // could never see again.
  const subscriberEditable = canWriteSubscriber(
    editable,
    canSearchResourceType(medplum, 'RelatedPerson'),
    details.relationship,
    details.policyholder
  );
  return (
    <Box data-testid="insurance-section">
      <Section title="Insurance" defaultOpen>
        <InsuranceCardRows card={card} details={details} labels={SECTION_LABELS} />
        {subscriberEditable ? (
          <SubscriberRow
            label="Subscriber"
            display={rows.subscriber.text}
            muted={rows.subscriber.muted}
            initial={subscriberFormValues(details.relationship, details.policyholder)}
            onSave={(subscriber) => saveEdit({ subscriber })}
          />
        ) : (
          <PanelRow label="Subscriber" value={rows.subscriber.text} muted={rows.subscriber.muted} />
        )}
      </Section>
    </Box>
  );
}
